
import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within a CartProvider');
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);

  useEffect(() => {
    try {
      const saved = sessionStorage.getItem('homeoCart');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          setCart(parsed.filter(item => item?.id && item.price != null));
        }
      }
    } catch (e) {
      console.error('Load cart failed:', e);
    }
  }, []);

  useEffect(() => {
    try {
      sessionStorage.setItem('homeoCart', JSON.stringify(cart));
    } catch (e) {
      console.error('Save cart failed:', e);
    }
  }, [cart]);

  const addToCart = (product) => {
    if (!product?.id) return;

    setCart(prev => {
      const exists = prev.find(p => p.id === product.id);
      if (exists) {
        if (exists.quantity >= exists.stock) {
          return prev;
        }
        return prev.map(p =>
          p.id === product.id
            ? { ...p, quantity: p.quantity + 1 }
            : p
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
  };

  const updateQuantity = (id, qty) => {
    const newQty = Number(qty);
    if (isNaN(newQty)) return;
    setCart(prev => {
      if (newQty <= 0) return prev.filter(p => p.id !== id);
      return prev.map(p => (p.id === id ? { ...p, quantity: newQty } : p));
    });
  };

  const removeFromCart = (id) => setCart(prev => prev.filter(p => p.id !== id));
  const clearCart = () => setCart([]);

  const calculations = useMemo(() => {
    const totals = cart.reduce(
      (acc, item) => {
        const price = Number(item.price) || 0;
        const qty = Number(item.quantity) || 1;
        const taxPerUnit = (price * (item.tax_percentage || 0)) / 100;
        const itemSubtotal = price * qty;
        const itemTax = taxPerUnit * qty;
        const itemTotal = itemSubtotal + itemTax;
        return {
          subtotal: acc.subtotal + itemSubtotal,
          tax: acc.tax + itemTax,
          total: acc.total + itemTotal,
          count: acc.count + qty,
        };
      },
      { subtotal: 0, tax: 0, total: 0, count: 0 }
    );
    return {
      subtotal: Number(totals.subtotal.toFixed(2)),
      taxAmount: Number(totals.tax.toFixed(2)),
      totalPrice: Number(totals.total.toFixed(2)),
      totalItems: totals.count,
    };
  }, [cart]);


  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        updateQuantity,
        removeFromCart,
        clearCart,
        ...calculations,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};